/** Response shapes matched to the backend serialisers: `backend/app/auth.py`
 *  (`/v1/me`), `backend/assetlib/api.py`, `backend/brand/api.py`,
 *  `backend/modeling/api.py` and the agent's routes. An ad's own shapes are in
 *  `api/ads.ts` and its neighbours. */

import type { FilmTemplate } from "./templates";

export interface Me {
  user: { id: string; email: string; display_name: string };
  org: { id: string; name: string };
  /** `available`/`held` are null when Accounts could not be reached. */
  balance: { available: number | null; held: number | null };
  limits: Record<string, number | boolean | null>;
  /** What is switched on for this environment (`backend/app/features.py`).
   *  Links, buttons and cards are hidden from it; no endpoint is probed. An
   *  older API that sends none counts as every feature off. */
  features?: Features;
}

export interface Features {
  assetlib: boolean;
  imports: boolean;
  stills: boolean;
  studio: boolean;
  generated_skins: boolean;
  sculpted: boolean;
  /** A voice or music provider is set (§30.4); nothing shows them until P26. */
  voice: boolean;
  music: boolean;
  /** Templates copied from a review link (§11.2 (3)); not in the first release. */
  templates_shared: boolean;
}

export interface LogoutResponse {
  end_session_url: string;
}

// ------------------------------------------------------------------ options

export type Aspect = "9:16" | "16:9" | "1:1" | "4:5";

// ---------------------------------------------------------- the 3D library

export type AssetKind =
  "shape" | "material" | "skin" | "variant" | "figure" | "action";

/** One item of `GET /v1/assetlib` (`backend/assetlib/api.py`). The keys after
 *  `favorite` depend on the kind. Sizes are metres and millimetres as stored;
 *  the interface shows centimetres (`assets/format.ts`). */
export interface AssetItem {
  kind: AssetKind;
  id: string;
  name: string;
  category: string;
  tags: string[];
  tier: "curated" | "org";
  origin: string;
  version: number;
  thumb_url: string | null;
  /** A look that no longer fits its object. */
  stale: boolean;
  favorite: boolean;
  /** shape, and variant (its object's; null when the object is gone). */
  size_m?: [number, number, number] | null;
  /** shape */
  slots?: string[];
  regions?: {
    slot: string;
    type: string;
    size_mm: [number, number];
    aspect: number;
  }[];
  /** material */
  surface?: string;
  tint?: string;
  /** skin */
  region_type?: string;
  aspect?: number;
  px?: [number, number];
  emissive?: boolean;
  /** figure (P29): who it is, in words ("woman", "young", "light"); its
   *  `size_m` and `slots` are given too. */
  words?: string[];
  /** variant */
  shape?: string;
  shape_name?: string;
  /** The ads it was made for (docs/SPEC.md §6.4; `[]` for a curated one). */
  made_for?: { ad_id: string; title: string }[];
}

export interface AssetList {
  items: AssetItem[];
  next_cursor: string | null;
  /** How many of what the other filters keep each category holds, in the
   *  vocabulary's order; a category with nothing is not listed. */
  categories?: { id: string; count: number }[];
}

/** An asset as another asset's detail carries it: `meta` is the stored
 *  `asset.json`, whole; `files` maps a fixed file name to a presigned GET that
 *  lasts 15 minutes (`backend/assetlib/api.py::asset_detail`). */
export interface NestedAsset {
  kind: AssetKind;
  id: string;
  version: number;
  tier: "curated" | "org";
  // The document's keys depend on the kind; the viewer reads them by name.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  meta: Record<string, any>;
  files: Record<string, string>;
}

/** The reply of `GET /v1/assetlib/{kind}/{id}`. `shape` is present only for a
 *  look, `try_on` only for a surface and an artwork. */
export interface AssetDetail extends NestedAsset {
  origin: string;
  defaults: {
    materials: Record<string, NestedAsset>;
    skins: Record<string, NestedAsset>;
  };
  shape?: NestedAsset & {
    origin: string;
    defaults: AssetDetail["defaults"];
    viewer: "full" | "proxy";
    has_generator: boolean;
  };
  viewer: "full" | "proxy";
  try_on?: {
    kind: "shape";
    id: string;
    name: string;
    thumb_url: string | null;
    slot: string;
  }[];
  used_in: { ad_id: string; created_at: string; title: string }[];
  used_in_count: number;
  dependents: { kind: AssetKind; id: string; name: string }[];
  can_delete: boolean;
  has_generator: boolean;
  favorite: boolean;
  stale: boolean;
  /** The ads it was made for (docs/SPEC.md §6.4). */
  made_for?: { ad_id: string; title: string }[];
}

/** Where an import stands ([imports §1.5]). `published` and `rejected` are
 *  final. */
export type ImportState =
  "uploaded" | "checking" | "review" | "published" | "rejected" | "failed";

/** The body's `license` of `POST /v1/assetlib/imports`: a choice, never a
 *  licence id. */
export interface ImportLicenceChoice {
  choice: "own" | "cc0" | "marketplace" | "other";
  marketplace?: string;
  url?: string;
  author?: string;
}

/** The reply of `POST /v1/assetlib/imports`: where the browser PUTs the file. */
export interface ImportTicket {
  import_id: string;
  put_url: string;
  expires_at: string;
}

/** One item of `GET /v1/assetlib/imports`. */
export interface ImportItem {
  id: string;
  name: string;
  state: ImportState;
  /** `harvest`: an object saved from an ad of the old path, before P25. */
  origin: "import" | "harvest";
  error_code: string | null;
  thumb_url: string | null;
  updated_at: string;
}

export interface ImportList {
  items: ImportItem[];
  next_cursor: null;
}

export interface ImportWarning {
  code: string;
  params?: Record<string, unknown> | null;
}

/** One surface of the model as the member set it ([imports §5.2]). */
export interface ImportDraftSlot {
  name: string;
  rename: string;
  use: "native" | "material" | "print";
  accepts: string[];
  material: string | null;
  reunwrap: boolean;
  baked: boolean;
}

/** The details the member edits before the model is added ([imports §5.2]). */
export interface ImportDraft {
  id: string;
  name: string;
  description: string;
  tags: string[];
  category: string;
  /** Null while a file in the wrong unit waits for its real height. */
  height_m: number | null;
  front: "-y" | "+x" | "+y" | "-x";
  mount: "floor" | "wall" | "ceiling";
  replaces: string | null;
  slots: ImportDraftSlot[];
}

/** The body of `PATCH /v1/assetlib/imports/{id}`: only what changed. A
 *  surface is named by its `name` and carries only its changed keys. */
export type ImportPatch = Partial<Omit<ImportDraft, "id" | "slots">> & {
  slots?: (Pick<ImportDraftSlot, "name"> & Partial<ImportDraftSlot>)[];
};

/** What the check found ([imports §5.1]); read-only. */
export interface ImportReport {
  size_m: [number, number, number];
  triangles: number;
  source_triangles: number;
  decimated: boolean;
  slots: {
    name: string;
    source_material: string;
    has_texture: boolean;
    coplanar: boolean;
    uv_overlap: boolean;
  }[];
  warnings: ImportWarning[];
  dropped: string[];
  duplicate_of: { kind: AssetKind; id: string; name: string } | null;
  draft: ImportDraft;
}

/** The reply of `GET /v1/assetlib/imports/{id}`. */
export interface ImportDetail {
  id: string;
  state: ImportState;
  origin: "import" | "harvest";
  name: string;
  bytes: number;
  /** When the import last changed state: a check counts its time from it. */
  updated_at: string;
  /** Why a check has not started: `paused` while checks are paused on our side. */
  waiting_reason: "paused" | null;
  license: {
    spdx: string;
    source: string;
    url: string | null;
    author: string | null;
  };
  error_code: string | null;
  error_params: Record<string, unknown> | null;
  report: ImportReport | null;
  draft: ImportDraft | null;
  thumb_url: string | null;
  uv_checks: { slot: string; url: string | null }[];
  front_views: { front: ImportDraft["front"]; url: string | null }[];
  materials: { id: string; name: string; surface: string }[];
  categories: string[];
  published: { kind: "shape"; id: string; version: number } | null;
}

/** One of the user's images chosen as artwork and not saved yet. */
export type PickedImage = {
  image_url: string;
  upload_id: string;
  name: string;
};

/** What differs from an object's defaults while the user dresses it. */
export interface Dressing {
  /** slot -> material id */
  materials: Record<string, string>;
  /** slot -> skin id, or a picture not saved yet */
  skins: Record<string, string | PickedImage>;
  /** slot -> "#rrggbb" */
  colors_hex: Record<string, string>;
  /** part -> degrees (never saved) */
  poses: Record<string, number>;
}

// ------------------------------------------------------------------ uploads

/** `POST /v1/uploads`: the browser PUTs the bytes to `put_url` itself. */
export interface UploadTicket {
  asset_id: string;
  put_url: string;
  expires_at: string;
}

export const UPLOAD_MIMES = ["image/png", "image/jpeg", "image/webp"] as const;
export const UPLOAD_MAX_BYTES = 25 * 1024 * 1024;

// --------------------------------------------------------------- brand kits

/** One palette entry (docs/15-product/brand-kits.md §2): a role from
 *  `BrandKits.roles` and a `#rrggbb` colour. */
export interface BrandColour {
  role: string;
  hex: string;
}

export interface BrandKit {
  id: string;
  name: string;
  logo_asset_id: string | null;
  palette: BrandColour[];
  fonts: string[];
  voice: string;
  tagline: string;
  url: string | null;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

/** `GET /v1/brand-kits`: the kits with the vocabulary the form is bound to. */
export interface BrandKits {
  items: BrandKit[];
  roles: string[];
  fonts: string[];
  max_colours: number;
}

/** The writable fields of a kit (`POST`/`PATCH /v1/brand-kits`). */
export interface BrandKitBody {
  name: string;
  logo_asset_id: string | null;
  palette: BrandColour[];
  fonts: string[];
  voice: string;
  tagline: string;
  url: string | null;
}

export const MAX_VOICE_CHARS = 500;

// ------------------------------------------------------------- review links

/** A review link as the owner sees it (no token; docs/15-product/review-links.md §3). */
export interface ShareLink {
  id: string;
  expires_at: string;
  revoked_at: string | null;
  allow_comments: boolean;
  /** §14.5's Allow reuse: another organisation may take the film's scenes. */
  reuse: boolean;
  created_at: string;
  active: boolean;
}

export interface ShareComment {
  id: string;
  author_name: string;
  body: string;
  /** Seconds into the film, or null for a general remark. */
  t: number | null;
  created_at: string;
}

/** `GET /v1/ads/{id}/share`. */
export interface ShareIndex {
  links: ShareLink[];
  comments: ShareComment[];
}

/** `POST /v1/ads/{id}/share` → 201: the token and URL are shown once. */
export interface CreateShareResponse extends ShareLink {
  token: string;
  url: string;
}

export const SHARE_DEFAULT_DAYS = 14;
export const SHARE_MAX_DAYS = 90;

/** `GET /v1/r/{token}`: the public page, nothing but the film and the
 *  thread. `video_url` is null when no video is ready. */
export interface PublicReview {
  title: string;
  video_url: string | null;
  aspect: string | null;
  length_s: number | null;
  allow_comments: boolean;
  expires_at: string;
  comments: ShareComment[];
  /** The link allows reuse and shared templates are on (P27-T01). */
  reuse?: boolean;
}

/** A scene of a film shared with reuse (`GET /v1/r/{token}/reuse`), framed
 *  in the browser from the film's manifest (P28-T10). */
export interface ReuseScene {
  id: string;
  name: string;
  seconds: number | null;
}

/** What a signed-in person may take from a film shared with reuse: its
 *  scenes and the templates made from it (as `GET /v1/templates` lists one). */
export interface ReuseOffer {
  ad_id: string;
  title: string;
  aspect: string | null;
  version: number | null;
  /** The current version's manifest, readable while the link allows reuse. */
  manifest_url: string | null;
  scenes: ReuseScene[];
  templates: FilmTemplate[];
}

export const MAX_AUTHOR_CHARS = 60;
export const MAX_COMMENT_CHARS = 2000;

// -------------------------------------------------------------------- agent

/** One BYO worker of the org, as `GET /v1/agent/status` reports it
 *  (docs/03 §6). `online` is a heartbeat in the last five minutes. */
export interface AgentInfo {
  device_id: string | null;
  gpu_class: string | null;
  blender_version: string | null;
  os: string | null;
  state: string;
  last_seen_at: string | null;
  online: boolean;
}

export interface AgentStatus {
  byo_enabled: boolean;
  agents: AgentInfo[];
  /** The agent version the API expects; the daemon's update check reads it. */
  version: string;
}

/** `POST /v1/agent/pair {user_code, name}`: approves the code the agent
 *  printed (RFC 8628 step 3) for this org. */
export interface PairResponse {
  device_code_id: string | null;
  org_id: string;
  name: string;
}

export interface AgentVersion {
  version: string;
  url: string;
}

// --------------------------------------------------------- the design studio

/** What one kind of instruction (a design's first, or a later one) usually
 *  costs and takes ([billing §4.8]): the median, and the range the page shows. */
export interface KindEstimate {
  credits: number;
  credits_low: number;
  credits_high: number;
  seconds: number;
}

/** What one instruction usually costs and takes, and what is held for it
 *  ([design-studio §9.3]). `hold` is what would be held now; below a kind's
 *  `credits` an instruction is refused (§15.1). */
export interface TurnEstimate {
  first: KindEstimate;
  next: KindEstimate;
  hold: number;
}

export type ModelState = "idle" | "working" | "publishing";
export type ModelEngine = "agent" | "mesh";
export type ModelSource = "agent" | "params" | "restore" | "remix" | "mesh";
export type ModelStatus =
  "queued" | "running" | "ok" | "failed" | "refused" | "cancelled";
export type ModelStep =
  "queued" | "thinking" | "building" | "checking" | "done";

/** One setting a design's code declares ([modelling-sdk §7.4]). Lengths are
 *  metres as stored; the page shows centimetres. */
export type ModelParam =
  | {
      kind: "param";
      name: string;
      default: number;
      min: number;
      max: number;
      step: number | null;
      unit: string;
      label: string | null;
    }
  | {
      kind: "choice";
      name: string;
      default: string;
      options: string[];
      label: string | null;
    }
  | { kind: "flag"; name: string; default: boolean; label: string | null };

export type ModelValues = Record<string, number | string | boolean>;

/** What the user pointed at when sending an instruction
 *  ([design-studio §9.4]); `point` needs `normal`. */
export interface ModelSelection {
  slot?: string;
  part?: string;
  point?: [number, number, number];
  normal?: [number, number, number];
  view?: {
    azimuth_deg: number;
    elevation_deg: number;
    distance_factor: number;
  };
}

/** What a design keeps on its surfaces: ids only, never an unsaved image. */
export interface ModelDressing {
  materials: Record<string, string>;
  skins: Record<string, string>;
  colors_hex: Record<string, string>;
}

/** One step of a design's history. A `remix` step's `instruction` is the
 *  name of the object it started from. */
export interface ModelRevision {
  n: number;
  parent_n: number | null;
  source: ModelSource;
  instruction: string | null;
  status: ModelStatus;
  error_code: string | null;
  refusal_reason: string | null;
  values: ModelValues | null;
  created_at: string;
  credits: number;
  view_url: string | null;
}

/** The step with the highest number, whatever became of it. */
export interface ModelLatest {
  n: number;
  source: ModelSource;
  status: ModelStatus;
  step: ModelStep;
  step_started_at: string | null;
  error_code: string | null;
  error_params: Record<string, unknown> | null;
  refusal_reason: string | null;
  instruction: string | null;
  credits: number;
}

/** The step that is shown. `asset` has the keys of an object's detail, so
 *  the 3D view and the surfaces panel read it unchanged. */
export interface ModelCurrent {
  n: number;
  source: ModelSource;
  status: ModelStatus;
  params: ModelParam[];
  values: ModelValues;
  dressing: ModelDressing;
  report: {
    size_m?: [number, number, number];
    triangles?: number;
    parts?: unknown[];
    slots?: unknown[];
    [key: string]: unknown;
  };
  asset: AssetDetail;
}

/** `GET /v1/models/{id}` (docs/07 §1, "The design studio"), also the
 *  socket's `hello`. `current` is null until a step succeeded. */
export interface ModelDetail {
  id: string;
  name: string;
  state: ModelState;
  refs: { id: string; url: string }[];
  real_size_m: number | null;
  engine: ModelEngine;
  engines: ModelEngine[];
  published: {
    kind: "shape";
    id: string;
    version: number;
    up_to_date: boolean;
  } | null;
  publish_error_code: string | null;
  current: ModelCurrent | null;
  latest: ModelLatest | null;
  revisions: ModelRevision[];
  turn_estimate: TurnEstimate;
  mesh_fee: number;
  mesh_provider: string | null;
  code_url: string | null;
  /** The ad the design is being made for (docs/SPEC.md §6.4): its banner's
   *  words. All three null without a thread or when the ad is gone. */
  for_ad?: ForAd | null;
  ad_title?: string | null;
  thing?: string | null;
}

/** A design's thread to an ad: the ad and the cast panel's row. */
export interface ForAd {
  ad_id: string;
  item: string;
}

export interface ModelListItem {
  id: string;
  name: string;
  thumb_url: string | null;
  state: ModelState;
  updated_at: string;
  published: boolean;
  built: boolean;
  engine: ModelEngine;
  for_ad?: ForAd | null;
  ad_title?: string | null;
  thing?: string | null;
}

/** `GET /v1/models`: one page of designs, and what the New design page
 *  needs before any design exists. */
export interface ModelList {
  items: ModelListItem[];
  next_cursor: string | null;
  engines: ModelEngine[];
  count: number;
  limit: number;
  turn_estimate: TurnEstimate;
  mesh_fee: number;
  mesh_provider: string | null;
}

/** What a design's socket sends ([design-studio §10.3]). */
export type ModelEvent =
  | { type: "hello"; project: ModelDetail }
  | { type: "thinking"; text: string }
  | {
      type: "revision";
      n: number;
      status: ModelStatus;
      step: ModelStep;
      step_started_at: string | null;
      error_code: string | null;
      error_params: Record<string, unknown> | null;
      refusal_reason: string | null;
    }
  | { type: "published"; kind: "shape"; id: string; version: number }
  /** The object's picture is there (it follows `published`). */
  | { type: "thumbnail"; kind: "shape"; id: string; version: number }
  | { type: "error"; code: string }
  | { type: "ping" };

// ------------------------------------------------------------ socket events
